import React, { useState, useEffect } from 'react';
import { db, storage } from '../../firebase';
import { collection, onSnapshot, addDoc, updateDoc, doc, deleteDoc, query, orderBy } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { Foundation } from '../../types';
import { 
  BookOpen, 
  Plus, 
  Trash2, 
  ChevronRight, 
  ChevronDown, 
  MessageSquare, 
  Cpu, 
  Users, 
  Award, 
  Sparkles, 
  ShieldCheck, 
  Settings, 
  FileEdit, 
  X,
  Image as ImageIcon,
  CheckCircle2,
  AlertTriangle,
  Upload
} from 'lucide-react';

interface CurriculumTabContentProps {
  onEdit: (id: string) => void;
}

// Defining the 6 core Graduate soft-skill pillars with descriptions, icons, and themes
export const PILLARS = [
  {
    id: 'Communication',
    label: 'Communication',
    description: 'Verbal, written, active listening, and business correspondence.',
    icon: MessageSquare,
    color: 'from-blue-500 to-sky-400',
    bg: 'bg-blue-50/50',
    border: 'border-blue-100',
    textColor: 'text-blue-700'
  },
  {
    id: 'Problem Solving',
    label: 'Problem Solving',
    description: 'Critical thinking, analysis, rapid diagnostics, and troubleshooting.',
    icon: Cpu,
    color: 'from-amber-500 to-yellow-400',
    bg: 'bg-amber-50/50',
    border: 'border-amber-100',
    textColor: 'text-amber-800'
  },
  {
    id: 'Leadership',
    label: 'Leadership',
    description: 'Self-direction, accountability, project management, and initiative.',
    icon: Award,
    color: 'from-purple-500 to-indigo-400',
    bg: 'bg-purple-50/50',
    border: 'border-purple-100',
    textColor: 'text-purple-700'
  },
  {
    id: 'Collaboration',
    label: 'Collaboration',
    description: 'Team dynamics, constructive feedback, empathy, and collective execution.',
    icon: Users,
    color: 'from-emerald-500 to-teal-400',
    bg: 'bg-emerald-50/50',
    border: 'border-emerald-100',
    textColor: 'text-emerald-700'
  },
  {
    id: 'Creativity',
    label: 'Creativity',
    description: 'Design thinking, rapid prototyping, business innovation, and adaptability.',
    icon: Sparkles,
    color: 'from-rose-500 to-pink-400',
    bg: 'bg-rose-50/50',
    border: 'border-rose-100',
    textColor: 'text-rose-700'
  },
  {
    id: 'Integrity',
    label: 'Integrity',
    description: 'Ethics, punctuality, professional code, and work standards.',
    icon: ShieldCheck,
    color: 'from-slate-600 to-slate-400',
    bg: 'bg-slate-50',
    border: 'border-slate-200',
    textColor: 'text-slate-700'
  }
];

const PRESET_COVERS = [
  'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?auto=format&fit=crop&w=600&q=80',
  'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=600&q=80',
  'https://images.unsplash.com/photo-1531538606174-0f90ff5dce83?auto=format&fit=crop&w=600&q=80',
  'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=600&q=80',
  'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?auto=format&fit=crop&w=600&q=80'
];

export const CurriculumTabContent: React.FC<CurriculumTabContentProps> = ({ onEdit }) => {
  const [foundations, setFoundations] = useState<Foundation[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedPillar, setExpandedPillar] = useState<string | null>('Communication');

  // Modal State for New & Edit Foundation
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [selectedFoundationId, setSelectedFoundationId] = useState<string | null>(null);

  // Form Fields State
  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formPillar, setFormPillar] = useState('Communication');
  const [formHeroImage, setFormHeroImage] = useState(PRESET_COVERS[0]);

  // Media Upload States for module cover image
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // Double-confirm delete safety gate
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  useEffect(() => {
    const q = query(collection(db, 'foundations'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => {
        const item = doc.data();
        return {
          id: doc.id,
          tenantId: item.tenantId || 'global',
          title: item.title || 'Untitled Module',
          description: item.description || '',
          pillar: item.pillar || 'Communication',
          heroImage: item.heroImage || PRESET_COVERS[Math.floor(Math.random() * PRESET_COVERS.length)],
          slides: item.slides || [],
          createdAt: item.createdAt || new Date().toISOString()
        } as Foundation;
      });
      setFoundations(data);
      setLoading(false);
    }, (err) => {
      console.error('Snapshot error in CurriculumTabContent:', err);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const handleCoverImageUpload = (file: File) => {
    if (!file) return;
    
    // Validate type: support image
    const isImage = file.type.startsWith('image/');

    if (!isImage) {
      setUploadError('Invalid file type. Please upload an image file.');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setUploadError(null);

    const storageRef = ref(storage, `wbl_foundations_covers/${Date.now()}_${file.name}`);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setUploadProgress(Math.round(progress));
      },
      (err) => {
        console.error('WBL cover image upload error:', err);
        setUploadError(`Upload failed: ${err.message}`);
        setIsUploading(false);
        setUploadProgress(null);
      },
      async () => {
        try {
          const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
          setFormHeroImage(downloadUrl);
          setIsUploading(false);
          setUploadProgress(null);
          setUploadError(null);
        } catch (e: any) {
          setUploadError(`Failed to fetch link: ${e.message}`);
          setIsUploading(false);
          setUploadProgress(null);
        }
      }
    );
  };

  const openCreateModal = () => {
    setModalMode('create');
    setFormTitle('');
    setFormDescription('');
    setFormPillar('Communication');
    setFormHeroImage(PRESET_COVERS[Math.floor(Math.random() * PRESET_COVERS.length)]);
    setSelectedFoundationId(null);
    setUploadProgress(null);
    setUploadError(null);
    setIsUploading(false);
    setIsModalOpen(true);
  };

  const openEditModal = (f: Foundation) => {
    setModalMode('edit');
    setFormTitle(f.title);
    setFormDescription(f.description);
    setFormPillar(f.pillar || 'Communication');
    setFormHeroImage(f.heroImage || PRESET_COVERS[0]);
    setSelectedFoundationId(f.id);
    setUploadProgress(null);
    setUploadError(null);
    setIsUploading(false);
    setIsModalOpen(true);
  };

  const handleSaveFoundation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formTitle.trim()) return;

    const payload = {
      title: formTitle,
      description: formDescription,
      pillar: formPillar,
      heroImage: formHeroImage,
      updatedAt: new Date().toISOString()
    };

    try {
      if (modalMode === 'create') {
        const newDoc = {
          ...payload,
          tenantId: 'global',
          slides: [],
          createdAt: new Date().toISOString()
        };
        await addDoc(collection(db, 'foundations'), newDoc);
        // Automatically expand the pillar where new node is created
        setExpandedPillar(formPillar);
      } else if (modalMode === 'edit' && selectedFoundationId) {
        await updateDoc(doc(db, 'foundations', selectedFoundationId), payload);
      }
      setIsModalOpen(false);
    } catch (err) {
      console.error('Error saving foundation module:', err);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'foundations', id));
      setDeleteConfirmId(null);
    } catch (err) {
      console.error('Error deleting foundation module:', err);
    }
  };

  if (loading) {
    return (
      <div className="h-64 flex flex-col justify-center items-center text-slate-400">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0094c8] mb-4"></div>
        <span className="text-sm font-semibold uppercase tracking-wider">Loading Curriculum Registry...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      
      {/* Top Banner & Control Deck */}
      <div className="bg-white rounded-3xl border border-slate-200 p-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 shadow-sm">
        <div>
          <h2 className="text-lg font-black text-slate-800 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-[#0094c8]" />
            FOUNDATIONS CURRICULUM CREATOR
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Design and sequence active soft-skill training pathways mapped directly to the Profile of the SC Graduate.
          </p>
        </div>
        <button
          onClick={openCreateModal}
          className="bg-[#0094c8] hover:bg-[#0081ae] text-white px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider flex items-center gap-2 shadow-lg shadow-[#0094c8]/20 transition-all transform active:scale-95"
        >
          <Plus className="w-4 h-4" />
          New Foundation
        </button>
      </div>

      {/* Accordions Grouped by the 6 Pillars */}
      <div className="space-y-4">
        {PILLARS.map((pillar) => {
          const PillarIcon = pillar.icon;
          const isExpanded = expandedPillar === pillar.id;
          const pillarModules = foundations.filter(f => f.pillar === pillar.id);

          return (
            <div 
              key={pillar.id}
              className={`bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden transition-all duration-300 ${
                isExpanded ? 'ring-1 ring-slate-300' : 'hover:border-slate-300'
              }`}
            >
              {/* Accordion Trigger Panel */}
              <button
                onClick={() => setExpandedPillar(isExpanded ? null : pillar.id)}
                className="w-full px-6 py-4 flex items-center justify-between text-left transition-colors hover:bg-slate-50"
              >
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${pillar.color} text-white flex items-center justify-center shadow-md`}>
                    <PillarIcon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-extrabold text-sm text-slate-800 tracking-tight flex items-center gap-2">
                      {pillar.label} Pillar
                      <span className="bg-slate-100 text-slate-500 text-[10px] font-black px-2 py-0.5 rounded-full">
                        {pillarModules.length} Active
                      </span>
                    </h3>
                    <p className="text-xs text-slate-400 mt-0.5 font-medium">{pillar.description}</p>
                  </div>
                </div>
                <div className="text-slate-400">
                  <ChevronDown className={`w-5 h-5 transform transition-transform duration-300 ${isExpanded ? 'rotate-180 text-[#0094c8]' : ''}`} />
                </div>
              </button>

              {/* Accordion Table Content */}
              {isExpanded && (
                <div className="border-t border-slate-100 bg-slate-50/20 p-5 animate-in fade-in duration-200">
                  {pillarModules.length === 0 ? (
                    <div className="text-center py-10 text-slate-400 bg-white rounded-xl border border-dashed border-slate-200">
                      <ImageIcon className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                      <p className="text-xs font-semibold">No modules in {pillar.label} yet.</p>
                      <button 
                        onClick={openCreateModal}
                        className="text-[#0094c8] text-[10px] font-bold mt-1 uppercase hover:underline"
                      >
                        Create the first module
                      </button>
                    </div>
                  ) : (
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className="bg-slate-50 text-[10px] font-black text-slate-400 uppercase tracking-wider border-b border-slate-200">
                            <th className="py-3 px-4 w-44">Module Cover</th>
                            <th className="py-3 px-4">Metadata / Details</th>
                            <th className="py-3 px-4 text-center w-24">Slides</th>
                            <th className="py-3 px-4 text-right w-64">Action Station</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 text-slate-700">
                          {pillarModules.map((f) => (
                            <tr key={f.id} className="hover:bg-slate-50/50 transition-colors">
                              {/* 16:9 Cover Column */}
                              <td className="py-4 px-4">
                                <div className="aspect-[16/9] w-36 rounded-lg bg-slate-100 border border-slate-200/60 overflow-hidden relative shadow-sm group">
                                  <img 
                                    src={f.heroImage || PRESET_COVERS[0]} 
                                    alt={f.title}
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                  />
                                  <div className="absolute inset-0 bg-black/10"></div>
                                </div>
                              </td>

                              {/* Title & Description */}
                              <td className="py-4 px-4 align-top">
                                <div className="space-y-1">
                                  <h4 className="font-bold text-xs text-slate-800 uppercase tracking-tight">{f.title}</h4>
                                  <p className="text-xs text-slate-500 line-clamp-2 leading-relaxed">{f.description || 'No description provided.'}</p>
                                </div>
                              </td>

                              {/* Slides count badge */}
                              <td className="py-4 px-4 text-center">
                                <span className="inline-block bg-sky-50 text-[#0094c8] text-[10px] font-extrabold px-3 py-1 rounded-full border border-sky-100">
                                  {f.slides?.length || 0} Slides
                                </span>
                              </td>

                              {/* Workstation Actions */}
                              <td className="py-4 px-4 text-right align-middle">
                                {deleteConfirmId === f.id ? (
                                  <div className="flex items-center justify-end gap-2 animate-in slide-in-from-right-2">
                                    <span className="text-[10px] text-rose-600 font-bold flex items-center gap-1">
                                      <AlertTriangle className="w-3.5 h-3.5" /> Double-confirm delete?
                                    </span>
                                    <button 
                                      onClick={() => handleDelete(f.id)}
                                      className="bg-rose-600 text-white px-3 py-1 rounded-lg text-[10px] font-bold uppercase hover:bg-rose-700"
                                    >
                                      Delete
                                    </button>
                                    <button 
                                      onClick={() => setDeleteConfirmId(null)}
                                      className="bg-slate-100 text-slate-600 px-3 py-1 rounded-lg text-[10px] font-bold uppercase hover:bg-slate-200"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                ) : (
                                  <div className="flex items-center justify-end gap-2">
                                    <button
                                      onClick={() => openEditModal(f)}
                                      className="p-2 border border-slate-200 hover:border-[#0094c8] text-slate-600 hover:text-[#0094c8] rounded-xl flex items-center gap-1.5 text-[10px] font-bold uppercase bg-white transition-all shadow-sm"
                                      title="Edit Module Cover/Title"
                                    >
                                      <Settings className="w-3.5 h-3.5" />
                                      Identity
                                    </button>
                                    <button
                                      onClick={() => onEdit(f.id)}
                                      className="p-2 bg-[#0094c8]/10 hover:bg-[#0094c8]/20 border border-[#0094c8]/25 text-[#0094c8] rounded-xl flex items-center gap-1.5 text-[10px] font-black uppercase transition-all shadow-sm"
                                      title="Launch Workspace Builder"
                                    >
                                      <FileEdit className="w-3.5 h-3.5" />
                                      Builder
                                    </button>
                                    <button
                                      onClick={() => setDeleteConfirmId(f.id)}
                                      className="p-2 border border-transparent hover:border-rose-200 text-slate-300 hover:text-rose-500 rounded-xl transition-all"
                                      title="Delete Curriculum Module"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Basic Metadata Modal: "Identity Editor" & "New Module Builder" */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl border border-slate-200 max-w-lg w-full overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200">
            
            {/* Modal Header */}
            <div className="px-6 py-4 bg-slate-50 border-b border-slate-100 flex justify-between items-center">
              <h3 className="text-sm font-black text-slate-800 uppercase tracking-wider flex items-center gap-2">
                <Settings className="w-4 h-4 text-[#0094c8]" />
                {modalMode === 'create' ? 'Create New Foundation Module' : 'Configure Module Identity'}
              </h3>
              <button 
                onClick={() => setIsModalOpen(false)}
                className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-all"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleSaveFoundation} className="p-6 space-y-5">
              
              {/* Title Field */}
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Module Title</label>
                <input 
                  type="text"
                  required
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                  placeholder="e.g. Active Listening in Client Correspondence"
                  className="w-full p-3 border border-slate-200 rounded-xl text-xs font-semibold focus:ring-2 focus:ring-sky-100 outline-none"
                />
              </div>

              {/* Pillar Selector */}
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Core Skill Pillar</label>
                <select
                  value={formPillar}
                  onChange={(e) => setFormPillar(e.target.value)}
                  className="w-full p-3 border border-slate-200 rounded-xl text-xs font-semibold focus:ring-2 focus:ring-sky-100 outline-none bg-white"
                >
                  {PILLARS.map(p => (
                    <option key={p.id} value={p.id}>{p.label} Pillar</option>
                  ))}
                </select>
              </div>

              {/* Description Field */}
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Summary Description</label>
                <textarea 
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  placeholder="Summarize the core learnings and work-based objectives of this specific training stage..."
                  rows={3}
                  className="w-full p-3 border border-slate-200 rounded-xl text-xs font-medium focus:ring-2 focus:ring-sky-100 outline-none"
                />
              </div>

              {/* Hero Cover Selection */}
              <div className="space-y-3">
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Hero Cover Image Link</label>
                  <input 
                    type="text"
                    value={formHeroImage}
                    onChange={(e) => setFormHeroImage(e.target.value)}
                    placeholder="https://images.unsplash.com/..."
                    className="w-full p-3 border border-slate-200 rounded-xl text-xs font-mono focus:ring-2 focus:ring-sky-100 outline-none"
                  />
                </div>

                {/* Cover Image Upload Area */}
                <div className="space-y-1.5">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Upload Custom Cover Image</span>
                  <div 
                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    onDrop={(e) => { e.preventDefault(); e.stopPropagation(); if (e.dataTransfer.files?.length) handleCoverImageUpload(e.dataTransfer.files[0]); }}
                    className="border-2 border-dashed border-slate-200 hover:border-[#00a3e0] bg-slate-50/50 hover:bg-slate-50 rounded-2xl p-4 text-center cursor-pointer transition-all relative group"
                  >
                    <input 
                      type="file"
                      accept="image/*"
                      className="absolute inset-0 opacity-0 cursor-pointer"
                      onChange={(e) => { if (e.target.files?.length) handleCoverImageUpload(e.target.files[0]); }}
                      disabled={isUploading}
                    />
                    
                    {isUploading ? (
                      <div className="space-y-1">
                        <div className="w-5 h-5 border-2 border-[#00a3e0] border-t-transparent rounded-full animate-spin mx-auto mb-1"></div>
                        <span className="text-[10px] text-slate-700 font-bold block">Uploading cover to Storage...</span>
                        <span className="text-[9px] text-[#00a3e0] font-black mt-1">{uploadProgress}%</span>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        <Upload className="w-5 h-5 text-slate-400 mx-auto group-hover:text-[#00a3e0] transition-colors" />
                        <span className="text-[10px] text-slate-700 font-bold block">Drag & drop your image or <span className="text-[#00a3e0] underline">browse</span></span>
                        <span className="text-[8px] text-slate-400 font-medium block">PNG, JPG or WEBP (Max 5MB)</span>
                      </div>
                    )}
                  </div>
                  {uploadError && (
                    <p className="text-[10px] text-rose-500 font-bold flex items-center gap-1">
                      <AlertTriangle className="w-3.5 h-3.5" /> {uploadError}
                    </p>
                  )}
                </div>
                
                {/* Preset Picker */}
                <div className="space-y-1.5">
                  <span className="text-[9px] font-extrabold text-slate-400 uppercase block">Or Choose standard WBL Co-op Cover Preset:</span>
                  <div className="grid grid-cols-5 gap-2">
                    {PRESET_COVERS.map((cov, cIdx) => (
                      <button
                        type="button"
                        key={cIdx}
                        onClick={() => setFormHeroImage(cov)}
                        className={`aspect-[16/9] rounded-lg border overflow-hidden relative transition-all ${
                          formHeroImage === cov ? 'ring-2 ring-[#00a3e0] scale-95 shadow-sm' : 'opacity-70 hover:opacity-100'
                        }`}
                      >
                        <img src={cov} alt="Preset Cover" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Form Actions */}
              <div className="pt-4 border-t border-slate-100 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl text-xs font-bold uppercase"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-[#0094c8] hover:bg-[#0081ae] text-white rounded-xl text-xs font-black uppercase shadow-md shadow-[#0094c8]/10"
                >
                  {modalMode === 'create' ? 'Build Module' : 'Sync Identity'}
                </button>
              </div>

            </form>

          </div>
        </div>
      )}

    </div>
  );
};
